<template>
  <Popover
    :is-open="isBasketFlyoutOpen"
    @touchstart="handleTouchStart"
    @mouseenter="handleMouseEnter"
    @touchend="handleTouchEnd"
    @mouseleave="handleMouseLeave"
  >
    <template #action>
      <Backdrop
        :show="isBasketFlyoutOpen"
        class="z-50"
        @click="closeBasketFlyout"
      />
      <AgsLink
        data-test-id="basket-link"
        data-qa="cart-icon"
        :to="toLocalePath({ name: 'basket' })"
        type="loud"
        :badge="count && count > 0 ? count : undefined"
        :class="{ 'pb-1': !isMobile }"
        @click="trackNavigationHeader($t('navigation.basket'))"
      >
        <template #icon>
          <IconShoppingCart class="mt-[-2px] size-6 scale-110" />
        </template>
        <span class="mt-0 hidden text-sm normal-case lg:flex">
          {{ $t('navigation.basket') }}
        </span>
      </AgsLink>
    </template>
    <template #content>
      <div @mouseleave="handleMouseLeave">
        <ClientOnly>
          <div
            v-if="items?.length"
            class="max-h-[calc(100vh-25rem)] overflow-auto sm:max-h-fit"
          >
            <article v-for="(item, index) in items" :key="item.key">
              <BasketCardPopover
                is-light-variant
                :product="item.product"
                :variant="item.variant"
                :title="getBasketCardPopoverTitle(item.product.attributes)"
                :size="getBasketCardPopoverSize(item.variant)"
                :color="getProductColors(item.product, 'color').join('/')"
                :article-no="item.variant.referenceKey || ''"
                :image-hash="getFrontImage(item.product.images).hash"
                :reduced-price="getDiscountPrice(item.price)"
                :price="item.price.total.withTax"
                :quantity="item.quantity"
                :available-quantity="item.availableQuantity"
                :show-price-reduction="isActiveSalePeriod"
                :is-sold-out="item.product.isSoldOut"
                :has-basket-action="false"
                :has-quantity-action="false"
                :has-wishlist-action="false"
                :order-number="
                  (item?.customData as BasketItemCustomData)?.ordernumber
                "
              />
              <hr
                v-if="items && index < items?.length - 1"
                class="bg-gray-500 w-full"
              />
            </article>
          </div>
          <div
            v-if="!items?.length"
            class="flex w-full min-w-[200px] flex-col items-center justify-center py-4"
            @mouseleave="handleMouseLeave"
          >
            <div class="w-2/3">
              <p class="text-center text-sm">
                {{ $t('basket.no_items_info') }}
              </p>
            </div>
          </div>
          <BasketCTAs v-if="items?.length" @close="closeBasketFlyout" />
        </ClientOnly>
      </div>
    </template>
  </Popover>
</template>

<script setup lang="ts">
import type { Variant, Attributes, BasketItem } from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
  getSizeFromVariant,
  getProductColors,
} from '@scayle/storefront-nuxt'
import type { BasketItemCustomData } from '~/types/basket'

const props = defineProps<{ items?: BasketItem[]; count?: number }>()

const { toLocalePath } = useRouteHelpers()
const { isMobile } = useDeviceType()
const { openBasketFlyout, closeBasketFlyout, isBasketFlyoutOpen } = useUiState()
const { trackNavigationHeader, trackBasket } = useTrackingEvents()

const { isActiveSalePeriod } = useSmartShopConfiguration()

const getDiscountPrice = (price: BasketItem['price']) =>
  getAppliedReductionsByCategory(price.total, 'sale')[0]?.amount.absoluteWithTax

const isTouch = ref(false)
const handleTouchStart = (e?: Event) => {
  e?.preventDefault()
  isTouch.value = true
}
const handleTouchEnd = () => {
  isTouch.value = true
}
const handleMouseEnter = () => {
  if (!isMobile.value) {
    openBasketFlyout()
    trackBasket({ basketItems: props.items ?? [] })
  }
}
const handleMouseLeave = () => {
  closeBasketFlyout()
}

const getBasketCardPopoverTitle = (attrs: Attributes | undefined) =>
  getFirstAttributeValue(attrs, 'name')?.label || ''
const getBasketCardPopoverSize = (variant: Variant) =>
  getSizeFromVariant(variant, 'size')?.label || ''
</script>
